import type { TutorialPage } from '../../../types/tutorial';

/**
 * Tutorial content for all Retail Partner Portal pages.
 * Each tutorial has a pageKey matching the route, a title, and steps.
 * Selectors use data-tutorial="selector-name" attributes on page elements.
 */

// Dashboard Tutorial
export const dashboardTutorial: TutorialPage = {
  pageKey: 'dashboard',
  title: 'Dashboard Overview',
  description: 'Learn about your store dashboard and quick actions',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'dashboard-welcome',
      title: 'Welcome to Your Dashboard',
      content: 'This is your store headquarters. Here you can see at-a-glance stats, recent activity, and quickly access common actions.',
      placement: 'center',
    },
    {
      id: 'dashboard-stats',
      title: 'Quick Stats',
      content: 'These cards show your key metrics: orders this month, pending quotes, Bridge Club members, and account balance.',
      targetSelector: '[data-tutorial="dashboard-stats"]',
      placement: 'bottom',
    },
    {
      id: 'dashboard-quick-actions',
      title: 'Quick Actions',
      content: 'Use these buttons to quickly place orders, view your account, manage Bridge Club, or access other features.',
      targetSelector: '[data-tutorial="quick-actions"]',
      placement: 'bottom',
    },
    {
      id: 'dashboard-recent-orders',
      title: 'Recent Orders',
      content: 'Your most recent orders appear here. Click any order to see full details and tracking information.',
      targetSelector: '[data-tutorial="recent-orders"]',
      placement: 'top',
    },
    {
      id: 'dashboard-help',
      title: 'Need Help?',
      content: 'You can restart this tutorial anytime by clicking the Help button in the page header. Each page has its own tutorial!',
      placement: 'center',
    },
  ],
};

// Place Order Tutorial
export const placeOrderTutorial: TutorialPage = {
  pageKey: 'place-order',
  title: 'Placing an Order',
  description: 'Learn how to place a new feed order',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'order-intro',
      title: 'Place a New Order',
      content: 'This page guides you through placing an order for feed products. Follow the steps to build and submit your order.',
      placement: 'center',
    },
    {
      id: 'order-delivery-date',
      title: 'Select Delivery Date',
      content: 'Choose when you want your order delivered. Available dates are shown based on our delivery schedule.',
      targetSelector: '[data-tutorial="delivery-date"]',
      placement: 'bottom',
    },
    {
      id: 'order-shipping',
      title: 'Shipping Method',
      content: 'Select your preferred shipping method. Pickup is also available if you prefer to collect from our location.',
      targetSelector: '[data-tutorial="shipping-method"]',
      placement: 'bottom',
    },
    {
      id: 'order-quick-add',
      title: 'Quick Add Products',
      content: 'Use the quick add section to search and add products to your order. You can search by name or SKU.',
      targetSelector: '[data-tutorial="quick-add-section"]',
      placement: 'bottom',
    },
    {
      id: 'order-line-items',
      title: 'Order Line Items',
      content: 'Your selected products appear here. Adjust quantities or remove items as needed.',
      targetSelector: '[data-tutorial="line-items"]',
      placement: 'top',
    },
    {
      id: 'order-add-line',
      title: 'Add More Items',
      content: 'Click here to add additional line items to your order.',
      targetSelector: '[data-tutorial="add-line-btn"]',
      placement: 'left',
    },
    {
      id: 'order-summary',
      title: 'Order Summary',
      content: 'Review your order total including any applicable taxes and shipping costs.',
      targetSelector: '[data-tutorial="order-summary"]',
      placement: 'top',
    },
    {
      id: 'order-submit',
      title: 'Submit Order',
      content: 'When ready, click Submit to complete your order. You\'ll receive a confirmation email.',
      targetSelector: '[data-tutorial="submit-btn"]',
      placement: 'top',
    },
  ],
};

// Orders Tutorial
export const ordersTutorial: TutorialPage = {
  pageKey: 'orders',
  title: 'Order Management',
  description: 'Track and manage your store orders',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'orders-intro',
      title: 'Your Orders',
      content: 'This page shows all your orders. You can filter, search, and track each order\'s status.',
      placement: 'center',
    },
    {
      id: 'orders-table',
      title: 'Orders Table',
      content: 'All your orders are listed here with key details. Click any order to see the full details.',
      targetSelector: '[data-tutorial="orders-table"]',
      placement: 'top',
    },
    {
      id: 'orders-status',
      title: 'Order Status',
      content: 'Status badges show where each order is in the process: Pending, Confirmed, In Production, Shipped, or Delivered.',
      targetSelector: '[data-tutorial="status-badge"]',
      placement: 'left',
    },
    {
      id: 'orders-actions',
      title: 'Order Actions',
      content: 'Use these buttons to view details, track shipments, or manage your orders.',
      targetSelector: '[data-tutorial="order-actions"]',
      placement: 'left',
    },
    {
      id: 'orders-tracking',
      title: 'Shipment Tracking',
      content: 'For shipped orders, click to view tracking information and estimated delivery dates.',
      targetSelector: '[data-tutorial="tracking-section"]',
      placement: 'bottom',
    },
  ],
};

// Quotes Tutorial
export const quotesTutorial: TutorialPage = {
  pageKey: 'quotes',
  title: 'Quote Management',
  description: 'View and respond to price quotes',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'quotes-intro',
      title: 'Your Quotes',
      content: 'Quotes are price estimates that need your approval before becoming orders. Review and accept quotes here.',
      placement: 'center',
    },
    {
      id: 'quotes-table',
      title: 'Quotes Table',
      content: 'All your quotes are listed here. Pending quotes require your action before they expire.',
      targetSelector: '[data-tutorial="quotes-table"]',
      placement: 'top',
    },
    {
      id: 'quotes-expiration',
      title: 'Expiration Dates',
      content: 'Watch the expiration column - quotes are only valid for a limited time. Don\'t miss out!',
      targetSelector: '[data-tutorial="expiration-column"]',
      placement: 'bottom',
    },
    {
      id: 'quotes-accept',
      title: 'Accept Quote',
      content: 'Click Accept to convert a quote into an order. You\'ll be able to select a delivery date.',
      targetSelector: '[data-tutorial="accept-btn"]',
      placement: 'left',
    },
  ],
};

// Bridge Club Tutorial
export const bridgeClubTutorial: TutorialPage = {
  pageKey: 'bridge-club',
  title: 'Bridge Club Management',
  description: 'Learn how to manage your store\'s Bridge Club loyalty program',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'bridge-intro',
      title: 'Bridge Club Overview',
      content: 'Bridge Club is a loyalty program where your customers earn points on purchases and redeem rewards. You manage it all from here.',
      placement: 'center',
    },
    {
      id: 'bridge-stats',
      title: 'Program Stats',
      content: 'View your Bridge Club performance at a glance: total members, points issued, and rewards redeemed.',
      targetSelector: '[data-tutorial="bridge-stats"]',
      placement: 'bottom',
    },
    {
      id: 'bridge-checkin',
      title: 'Check-In Tab',
      content: 'Use this tab to check in members when they visit your store.',
      targetSelector: '[data-tutorial="checkin-tab"]',
      placement: 'bottom',
    },
    {
      id: 'bridge-scan',
      title: 'Scan QR Code',
      content: 'Scan a member\'s QR code to quickly look them up and record their purchase.',
      targetSelector: '[data-tutorial="scan-qr-btn"]',
      placement: 'bottom',
    },
    {
      id: 'bridge-search',
      title: 'Search Members',
      content: 'Search for members by name, email, or phone number.',
      targetSelector: '[data-tutorial="member-search"]',
      placement: 'bottom',
    },
    {
      id: 'bridge-purchase',
      title: 'Record Purchase',
      content: 'Enter the purchase amount to award points to the member.',
      targetSelector: '[data-tutorial="record-purchase"]',
      placement: 'left',
    },
    {
      id: 'bridge-redeem',
      title: 'Redeem Rewards',
      content: 'Help members redeem their points for rewards and discounts.',
      targetSelector: '[data-tutorial="redeem-btn"]',
      placement: 'left',
    },
    {
      id: 'bridge-analytics',
      title: 'Analytics Tab',
      content: 'Check the Analytics tab for detailed program insights and trends.',
      targetSelector: '[data-tutorial="analytics-tab"]',
      placement: 'bottom',
    },
  ],
};

// Billing Tutorial
export const billingTutorial: TutorialPage = {
  pageKey: 'billing',
  title: 'Billing & Invoices',
  description: 'Manage your invoices and payments',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'billing-intro',
      title: 'Billing Overview',
      content: 'Manage your account balance, view invoices, and make payments all in one place.',
      placement: 'center',
    },
    {
      id: 'billing-balance',
      title: 'Account Balance',
      content: 'Your current balance and credit details are displayed here. Keep track of what\'s due.',
      targetSelector: '[data-tutorial="balance-cards"]',
      placement: 'bottom',
    },
    {
      id: 'billing-invoices',
      title: 'Invoice List',
      content: 'All your invoices are listed here. Click any invoice to view details and download a PDF.',
      targetSelector: '[data-tutorial="invoice-table"]',
      placement: 'top',
    },
    {
      id: 'billing-pay',
      title: 'Make a Payment',
      content: 'Click to make a payment toward your balance. You can pay specific invoices or your total balance.',
      targetSelector: '[data-tutorial="pay-btn"]',
      placement: 'left',
    },
    {
      id: 'billing-credits',
      title: 'Credits Tab',
      content: 'View any credits or adjustments applied to your account.',
      targetSelector: '[data-tutorial="credits-tab"]',
      placement: 'bottom',
    },
  ],
};

// Tax Exemption Tutorial
export const taxExemptionTutorial: TutorialPage = {
  pageKey: 'tax-exemption',
  title: 'Tax Exemption',
  description: 'Manage your tax exempt status',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'tax-exemption-intro',
      title: 'Tax Exemption Status',
      content: 'This page shows your current tax exemption status and allows you to manage your certificates.',
      placement: 'center',
    },
    {
      id: 'tax-status-card',
      title: 'Current Status',
      content: 'Your tax exemption status is shown here. Green means approved, yellow means pending review.',
      targetSelector: '[data-tutorial="tax-status-card"]',
      placement: 'bottom',
    },
    {
      id: 'tax-expiration',
      title: 'Expiration Alert',
      content: 'Watch for expiration warnings. You\'ll need to upload a new certificate before your current one expires.',
      targetSelector: '[data-tutorial="expiration-alert"]',
      placement: 'bottom',
    },
    {
      id: 'tax-upload',
      title: 'Upload Certificate',
      content: 'Upload your tax exemption certificate here. Accepted formats are PDF, JPG, and PNG.',
      targetSelector: '[data-tutorial="upload-section"]',
      placement: 'top',
    },
  ],
};

// Tax Documents Tutorial
export const taxDocsTutorial: TutorialPage = {
  pageKey: 'tax-documents',
  title: 'Tax Documents',
  description: 'Upload and manage tax exemption documents',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'tax-docs-intro',
      title: 'Tax Documents',
      content: 'Manage all your tax exemption documents in one place.',
      placement: 'center',
    },
    {
      id: 'tax-docs-list',
      title: 'Documents List',
      content: 'All your uploaded tax documents are shown here with their approval status.',
      targetSelector: '[data-tutorial="documents-list"]',
      placement: 'bottom',
    },
    {
      id: 'tax-docs-upload',
      title: 'Upload New Document',
      content: 'Click to upload a new tax certificate or exemption document.',
      targetSelector: '[data-tutorial="upload-btn"]',
      placement: 'left',
    },
    {
      id: 'tax-docs-type',
      title: 'Document Type',
      content: 'Select the type of document you\'re uploading for proper categorization.',
      targetSelector: '[data-tutorial="doc-type-select"]',
      placement: 'bottom',
    },
  ],
};

// Team Management Tutorial
export const teamTutorial: TutorialPage = {
  pageKey: 'team',
  title: 'Team Management',
  description: 'Manage your store team members and access',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'team-intro',
      title: 'Manage Your Team',
      content: 'Add team members to give them access to your store portal. Each member can have different permissions.',
      placement: 'center',
    },
    {
      id: 'team-table',
      title: 'Team Members',
      content: 'Your current team is listed here with their roles and last activity.',
      targetSelector: '[data-tutorial="team-table"]',
      placement: 'bottom',
    },
    {
      id: 'team-invite',
      title: 'Invite New Members',
      content: 'Click to invite a new team member. They\'ll receive an email to set up their account.',
      targetSelector: '[data-tutorial="invite-btn"]',
      placement: 'left',
    },
    {
      id: 'team-roles',
      title: 'Assign Roles',
      content: 'Use the role selector to control what each member can do: Owner, Manager, Staff, Cashier, or Viewer.',
      targetSelector: '[data-tutorial="role-select"]',
      placement: 'bottom',
    },
    {
      id: 'team-actions',
      title: 'Member Actions',
      content: 'Use these buttons to edit permissions, resend invites, or remove team members.',
      targetSelector: '[data-tutorial="member-actions"]',
      placement: 'left',
    },
  ],
};

// Settings Tutorial
export const settingsTutorial: TutorialPage = {
  pageKey: 'settings',
  title: 'Store Settings',
  description: 'Configure your store preferences',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'settings-intro',
      title: 'Store Settings',
      content: 'Configure your store preferences, delivery settings, and equipment information.',
      placement: 'center',
    },
    {
      id: 'settings-equipment',
      title: 'Equipment Info',
      content: 'Tell us about your receiving equipment so we can ensure smooth deliveries.',
      targetSelector: '[data-tutorial="equipment-section"]',
      placement: 'bottom',
    },
    {
      id: 'settings-shipping',
      title: 'Shipping Preferences',
      content: 'Set your preferred delivery days and times. We\'ll schedule deliveries to match your preferences when possible.',
      targetSelector: '[data-tutorial="shipping-section"]',
      placement: 'top',
    },
  ],
};

// API Integration Tutorial
export const apiTutorial: TutorialPage = {
  pageKey: 'api-integration',
  title: 'API Integration',
  description: 'Connect your POS system to our API',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'api-intro',
      title: 'API Integration',
      content: 'Connect your point-of-sale system to automatically sync orders and inventory.',
      placement: 'center',
    },
    {
      id: 'api-keys',
      title: 'API Keys',
      content: 'Generate and manage API keys to authenticate your system. Keep these secure!',
      targetSelector: '[data-tutorial="api-keys-section"]',
      placement: 'bottom',
    },
    {
      id: 'api-webhooks',
      title: 'Webhooks',
      content: 'Set up webhooks to receive real-time notifications when orders ship or invoices are created.',
      targetSelector: '[data-tutorial="webhooks-section"]',
      placement: 'top',
    },
    {
      id: 'api-guides',
      title: 'POS Guides',
      content: 'Follow our step-by-step guides to integrate with popular POS systems.',
      targetSelector: '[data-tutorial="pos-guides"]',
      placement: 'bottom',
    },
    {
      id: 'api-activity',
      title: 'Activity Log',
      content: 'Monitor API activity and troubleshoot integration issues.',
      targetSelector: '[data-tutorial="activity-log"]',
      placement: 'top',
    },
  ],
};

// Order History Tutorial
export const orderHistoryTutorial: TutorialPage = {
  pageKey: 'order-history',
  title: 'Order History',
  description: 'View your complete order history',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'history-intro',
      title: 'Order History',
      content: 'Access your complete order history and reorder from past purchases.',
      placement: 'center',
    },
    {
      id: 'history-filters',
      title: 'Filter & Search',
      content: 'Filter orders by date range, status, or search for specific products.',
      targetSelector: '[data-tutorial="history-filters"]',
      placement: 'bottom',
    },
    {
      id: 'history-card',
      title: 'Order Details',
      content: 'Each order shows key details. Click to expand and see all items.',
      targetSelector: '[data-tutorial="order-card"]',
      placement: 'bottom',
    },
    {
      id: 'history-reorder',
      title: 'Quick Reorder',
      content: 'Click Reorder to quickly add items from a past order to a new order.',
      targetSelector: '[data-tutorial="reorder-btn"]',
      placement: 'left',
    },
  ],
};

// Annual Report Tutorial
export const annualReportTutorial: TutorialPage = {
  pageKey: 'annual-report',
  title: 'Annual Report',
  description: 'View your annual purchasing summary',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'annual-intro',
      title: 'Annual Report',
      content: 'Get a comprehensive summary of your purchasing activity for any year.',
      placement: 'center',
    },
    {
      id: 'annual-stats',
      title: 'Year Statistics',
      content: 'See your total spending, order count, and most purchased products at a glance.',
      targetSelector: '[data-tutorial="annual-stats"]',
      placement: 'bottom',
    },
    {
      id: 'annual-charts',
      title: 'Visual Charts',
      content: 'Charts show your purchasing trends throughout the year.',
      targetSelector: '[data-tutorial="annual-charts"]',
      placement: 'top',
    },
    {
      id: 'annual-download',
      title: 'Download Report',
      content: 'Download your annual report as a PDF for your records.',
      targetSelector: '[data-tutorial="download-pdf-btn"]',
      placement: 'left',
    },
  ],
};

// Reorder Suggestions Tutorial
export const reorderTutorial: TutorialPage = {
  pageKey: 'reorder-suggestions',
  title: 'Reorder Suggestions',
  description: 'AI-powered reorder recommendations',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'reorder-intro',
      title: 'Smart Reorder Suggestions',
      content: 'Our AI analyzes your purchase history to suggest when you might need to reorder products.',
      placement: 'center',
    },
    {
      id: 'reorder-list',
      title: 'Suggested Items',
      content: 'These products are predicted to run low soon based on your typical ordering patterns.',
      targetSelector: '[data-tutorial="suggestions-list"]',
      placement: 'bottom',
    },
    {
      id: 'reorder-checkbox',
      title: 'Select Items',
      content: 'Check the items you want to reorder. You can select multiple items at once.',
      targetSelector: '[data-tutorial="suggestion-checkbox"]',
      placement: 'left',
    },
    {
      id: 'reorder-add',
      title: 'Add to Order',
      content: 'Click to add selected items to a new order with recommended quantities.',
      targetSelector: '[data-tutorial="add-to-order-btn"]',
      placement: 'top',
    },
  ],
};

// Resources Tutorial
export const resourcesTutorial: TutorialPage = {
  pageKey: 'resources',
  title: 'Resource Library',
  description: 'Access training materials and documents',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'resources-intro',
      title: 'Resource Library',
      content: 'Find training videos, product guides, and other helpful resources for your store.',
      placement: 'center',
    },
    {
      id: 'resources-featured',
      title: 'Featured Content',
      content: 'Check out our featured resources - these are recommended for all retail partners.',
      targetSelector: '[data-tutorial="featured-carousel"]',
      placement: 'bottom',
    },
    {
      id: 'resources-folders',
      title: 'Browse Categories',
      content: 'Resources are organized into folders by category. Click a folder to see its contents.',
      targetSelector: '[data-tutorial="folder-grid"]',
      placement: 'top',
    },
    {
      id: 'resources-card',
      title: 'View Resources',
      content: 'Click any resource to view it. Videos play inline, documents can be downloaded.',
      targetSelector: '[data-tutorial="resource-card"]',
      placement: 'bottom',
    },
  ],
};

// My Documents Tutorial (Retail Partner view)
export const myDocumentsTutorial: TutorialPage = {
  pageKey: 'my-documents',
  title: 'My Documents',
  description: 'Access your account documents',
  requiredCustomerType: 'RETAIL_PARTNER',
  steps: [
    {
      id: 'docs-intro',
      title: 'My Documents',
      content: 'This is where you\'ll find documents specific to your account, such as contracts and certificates.',
      placement: 'center',
    },
    {
      id: 'docs-categories',
      title: 'Document Categories',
      content: 'Documents are organized by type. Click a tab to filter by category.',
      targetSelector: '[data-tutorial="doc-filters"]',
      placement: 'bottom',
    },
    {
      id: 'docs-download',
      title: 'Download Documents',
      content: 'Click to download any document to your device.',
      targetSelector: '[data-tutorial="download-btn"]',
      placement: 'left',
    },
  ],
};

// My Documents Tutorial (Farm view — includes upload)
export const farmDocumentsTutorial: TutorialPage = {
  pageKey: 'my-documents',
  title: 'My Documents',
  description: 'Upload and access your farm documents',
  requiredCustomerType: 'WHOLESALE_FARM',
  steps: [
    {
      id: 'farm-docs-intro',
      title: 'Your Documents',
      content: 'Upload price sheets, farm records, or any documents here. Documents from Red Bridge appear in the lower section.',
      placement: 'center',
    },
    {
      id: 'farm-docs-upload',
      title: 'Upload Documents',
      content: 'Tap the upload area to add files. Laura can read price sheets and learn your prices automatically.',
      targetSelector: '[data-tutorial="upload-area"]',
      placement: 'bottom',
    },
    {
      id: 'farm-docs-redbridge',
      title: 'From Red Bridge',
      content: 'Contracts, agreements, and other documents from Red Bridge appear here.',
      targetSelector: '[data-tutorial="doc-grid"]',
      placement: 'top',
    },
  ],
};

// ================================================================
// FARMHAND TUTORIALS (WHOLESALE_FARM customers)
// ================================================================

// Farmhand Dashboard Tutorial
export const farmhandDashboardTutorial: TutorialPage = {
  pageKey: 'farmhand-dashboard',
  title: 'Farmhand Dashboard',
  description: 'Your farm command center — voice logging, plans, and batches',
  requiredCustomerType: 'WHOLESALE_FARM',
  steps: [
    {
      id: 'fhd-welcome',
      title: 'Welcome to Farmhand',
      content: 'This is your farm\'s command center. Record voice notes, check on your batches, and manage your seasonal plans — all from here.',
      placement: 'center',
    },
    {
      id: 'fhd-voice-button',
      title: 'Quick Voice Log',
      content: 'Tap this red button to start recording. Speak naturally — Laura will sort your notes into eggs, deaths, sales, weights, and more.',
      targetSelector: '[data-tutorial="quick-voice-log"]',
      placement: 'bottom',
    },
    {
      id: 'fhd-voice-tips',
      title: 'What to Say',
      content: 'Try things like "collected 40 eggs" or "lost a bird to a hawk in the north pasture" or "sold 10 dozen at the farmers market for 6 bucks a dozen." Laura handles the rest.',
      placement: 'center',
    },
    {
      id: 'fhd-offline',
      title: 'Works Offline',
      content: 'No signal in the back pasture? No problem. Record notes anywhere — they\'ll automatically sync when you\'re back online. A badge shows how many are waiting.',
      placement: 'center',
    },
    {
      id: 'fhd-plans',
      title: 'Your Plans',
      content: 'Your seasonal plans and active batches are listed here. Tap any plan to see details, log entries, and financials.',
      targetSelector: '[data-tutorial="your-plans"]',
      placement: 'top',
    },
  ],
};

// Voice Note Recording Tutorial (used from QuickVoiceLog)
export const voiceNoteTutorial: TutorialPage = {
  pageKey: 'voice-recording',
  title: 'Recording Voice Notes',
  description: 'How to use Laura for hands-free farm logging',
  requiredCustomerType: 'WHOLESALE_FARM',
  steps: [
    {
      id: 'voice-intro',
      title: 'Talk to Laura',
      content: 'Tap the red button and speak naturally. Laura listens, figures out what you said, and sorts it into the right categories and batches.',
      placement: 'center',
    },
    {
      id: 'voice-examples',
      title: 'What You Can Log',
      content: 'Eggs collected, deaths (with predator type), sales (with prices), costs, weights, milk, movements, medications — Laura understands them all.',
      placement: 'center',
    },
    {
      id: 'voice-review',
      title: 'Review Before Saving',
      content: 'After recording, Laura shows you what she found. Check or uncheck entries, answer any follow-up questions, then tap Save.',
      placement: 'center',
    },
    {
      id: 'voice-continue',
      title: 'Add More',
      content: 'After Laura shows her results, you can tap the microphone again to add more notes. Say "that\'s everything" when you\'re done.',
      placement: 'center',
    },
    {
      id: 'voice-laura',
      title: 'Laura Can Talk Back',
      content: 'Want hands-free? Enable Laura Voice in Settings. She\'ll read back what she found and ask follow-up questions out loud.',
      placement: 'center',
    },
  ],
};

// Batch Entry Edit/Delete Tutorial
export const entryEditTutorial: TutorialPage = {
  pageKey: 'entry-history',
  title: 'Editing Log Entries',
  description: 'Fix mistakes or remove entries from your log',
  requiredCustomerType: 'WHOLESALE_FARM',
  steps: [
    {
      id: 'edit-intro',
      title: 'Edit Your Entries',
      content: 'Made a mistake? You can unlock, edit, or delete any log entry. Here\'s how.',
      placement: 'center',
    },
    {
      id: 'edit-unlock',
      title: 'Unlock an Entry',
      content: 'Tap the lock icon on any entry to unlock it for editing. Entries re-lock automatically after 60 seconds.',
      targetSelector: '[data-tutorial="lock-indicator"]',
      placement: 'left',
    },
    {
      id: 'edit-buttons',
      title: 'Edit or Delete',
      content: 'Once unlocked, use the pencil to edit details or the trash can to delete the entry. Deleted entries are removed from your financials too.',
      targetSelector: '[data-tutorial="entry-actions"]',
      placement: 'left',
    },
    {
      id: 'edit-edited',
      title: 'Edit History',
      content: 'Edited entries show a small note with who edited them and when, so you always have a clear record.',
      placement: 'center',
    },
  ],
};

// Settings Tutorial (Farm view — Laura voice + prices)
export const farmSettingsTutorial: TutorialPage = {
  pageKey: 'settings',
  title: 'Farm Settings',
  description: 'Manage Laura voice, prices, and preferences',
  requiredCustomerType: 'WHOLESALE_FARM',
  steps: [
    {
      id: 'farm-settings-intro',
      title: 'Your Settings',
      content: 'Configure your delivery preferences, Laura\'s voice, and your price book all from here.',
      placement: 'center',
    },
    {
      id: 'farm-settings-prices',
      title: 'Your Prices',
      content: 'Laura learns your prices from sales you log. You can also add or edit prices here manually. When you log a sale without stating a price, Laura fills in your usual rate.',
      targetSelector: '[data-tutorial="price-book"]',
      placement: 'top',
    },
    {
      id: 'farm-settings-save',
      title: 'Save Changes',
      content: 'Don\'t forget to save after making changes to your delivery or shipping preferences.',
      targetSelector: '[data-tutorial="save-settings"]',
      placement: 'top',
    },
  ],
};

// Collection of all tutorials
export const tutorialData: TutorialPage[] = [
  // Retail Partner tutorials
  dashboardTutorial,
  placeOrderTutorial,
  ordersTutorial,
  quotesTutorial,
  bridgeClubTutorial,
  billingTutorial,
  taxExemptionTutorial,
  taxDocsTutorial,
  teamTutorial,
  settingsTutorial,
  apiTutorial,
  orderHistoryTutorial,
  annualReportTutorial,
  reorderTutorial,
  resourcesTutorial,
  myDocumentsTutorial,
  // Farmhand tutorials (WHOLESALE_FARM)
  farmDocumentsTutorial,
  farmhandDashboardTutorial,
  voiceNoteTutorial,
  entryEditTutorial,
  farmSettingsTutorial,
];

export default tutorialData;
